import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Boxes, Pencil, Trash2, Users, Workflow } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDeleteTeam, useTeam, useTeamAutomations } from "@/api/queries/teams"
import { useConfirm } from "@/components/app/confirm-dialog"
import { PageContainer, PageHeader } from "@/components/app/page"
import { ErrorState, FormSkeleton } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AutomationsPanel } from "@/features/teams/automations-panel"
import type { DesignerMode } from "@/features/teams/designer"
import { DesignerSheet } from "@/features/teams/designer-sheet"
import { MembersPanel } from "@/features/teams/members-panel"
import { RoomsPanel } from "@/features/teams/rooms-panel"
import { TeamDialog } from "@/features/teams/team-dialog"

const TEAM_TABS = ["members", "rooms", "automations"] as const
type TeamTab = (typeof TEAM_TABS)[number]

interface TeamSearch {
  tab?: Exclude<TeamTab, "members">
}

export const Route = createFileRoute("/_app/studio/teams/$teamId")({
  staticData: { titleKey: "studio.teams.detailTitle" },
  validateSearch: (s: Record<string, unknown>): TeamSearch => ({
    tab: s.tab === "rooms" || s.tab === "automations" ? s.tab : undefined,
  }),
  component: TeamPage,
})

function TabCount({ n }: { n: number | undefined }) {
  if (!n) return null
  return <span className="text-muted-foreground text-xs tabular-nums">{n}</span>
}

function TeamPage() {
  const { t } = useTranslation()
  const { teamId } = Route.useParams()
  const id = Number(teamId)
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const team = useTeam(id)
  const automations = useTeamAutomations()
  const remove = useDeleteTeam()
  const confirm = useConfirm()
  const [editOpen, setEditOpen] = useState(false)
  const [designer, setDesigner] = useState<{ open: boolean; mode: DesignerMode }>({
    open: false,
    mode: "builder",
  })
  const tab: TeamTab = search.tab ?? "members"

  if (team.isPending)
    return (
      <PageContainer width="wide">
        <div className="mb-8 flex items-center gap-3" aria-hidden>
          <Skeleton className="size-9 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-3.5 w-72" />
          </div>
        </div>
        <FormSkeleton fields={4} />
      </PageContainer>
    )

  if (team.isError)
    return (
      <PageContainer>
        <ErrorState error={team.error} onRetry={() => void team.refetch()} className="mt-10" />
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/studio/teams" />}>
            <ArrowLeft />
            {t("studio.teams.backToList")}
          </Button>
        </div>
      </PageContainer>
    )

  const data = team.data
  const automationCount = automations.data?.filter((a) => a.team_id === id).length

  const onDelete = () =>
    void confirm({
      title: t("studio.teams.deleteTitle", { name: data.name }),
      description:
        data.room_count > 0
          ? t("studio.teams.deleteInUse", { count: data.room_count })
          : t("studio.teams.deleteDescription"),
      confirmLabel: t("studio.teams.delete"),
      destructive: true,
      confirmText: data.name,
      onConfirm: async () => {
        await remove.mutateAsync(data)
        void navigate({ to: "/studio/teams" })
      },
    })

  return (
    <PageContainer width="wide">
      <PageHeader
        title={data.name}
        description={data.description || t("studio.teams.noDescription")}
        icon={Users}
        breadcrumbs={[{ label: t("studio.teams.title"), to: "/studio/teams" }, { label: data.name }]}
        badge={
          data.ready ? (
            <StatusBadge tone="success" dot>
              {t("studio.teams.ready")}
            </StatusBadge>
          ) : (
            <StatusBadge tone="warning" dot title={data.ready_error ?? undefined}>
              {t("studio.teams.notReady")}
            </StatusBadge>
          )
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="team-edit">
              <Pencil />
              {t("common.edit")}
            </Button>
            <Button variant="outline" size="icon" onClick={onDelete} aria-label={t("studio.teams.delete")}>
              <Trash2 />
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground text-xs">
          {t("studio.teams.concurrency", { count: data.max_concurrent_invocations })}
        </p>
      </PageHeader>

      <Tabs
        value={tab}
        onValueChange={(v) =>
          void navigate({
            search: { tab: v === "rooms" || v === "automations" ? v : undefined },
            replace: true,
          })
        }
        className="gap-5"
      >
        <div className="-mx-4 overflow-x-auto border-b px-4 sm:mx-0 sm:px-0">
          <TabsList variant="line" className="h-9">
            <TabsTrigger value="members" className="px-2">
              <Users />
              {t("studio.teams.tabs.members")}
              <TabCount n={data.members.length} />
            </TabsTrigger>
            <TabsTrigger value="rooms" className="px-2">
              <Boxes />
              {t("studio.teams.tabs.rooms")}
              <TabCount n={data.room_count} />
            </TabsTrigger>
            <TabsTrigger value="automations" className="px-2">
              <Workflow />
              {t("studio.teams.tabs.automations")}
              <TabCount n={automationCount} />
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="members">
          <MembersPanel team={data} onDesigner={(mode) => setDesigner({ open: true, mode })} />
        </TabsContent>
        <TabsContent value="rooms">
          <RoomsPanel teamId={id} />
        </TabsContent>
        <TabsContent value="automations">
          <AutomationsPanel teamId={id} />
        </TabsContent>
      </Tabs>

      <TeamDialog open={editOpen} onOpenChange={setEditOpen} team={data} />
      <DesignerSheet
        team={data}
        mode={designer.mode}
        open={designer.open}
        onOpenChange={(open) => setDesigner((d) => ({ ...d, open }))}
      />
    </PageContainer>
  )
}
